import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import styles from './CheckoutPage.module.css';
import sharedStyles from '../styles/SharedComponents.module.css';
import { supabase } from '../services/supabase';

// CheckoutForm component
const CheckoutForm = ({ orderId, totalAmount, restaurantId, onSuccess, stripeAccount }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [succeeded, setSucceeded] = useState(false);
  const [tipAmount, setTipAmount] = useState(0);
  const [customTipMode, setCustomTipMode] = useState(false);
  
  // Calculate total with tip
  const totalWithTip = parseFloat(totalAmount) + parseFloat(tipAmount || 0);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Create a payment intent on the server
      const response = await fetch('/create-payment-intent', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: totalAmount,
          orderId,
          tipAmount: tipAmount
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Payment failed');
      }

      const data = await response.json();
      console.log("Payment intent created, clientSecret received:", data.clientSecret);
      console.log("stripeAccount for direct charges:", data.stripeAccount);

      // For Direct Charges: confirmCardPayment WITHOUT stripeAccount parameter
      // because we initialized Stripe with the connected account
      const result = await stripe.confirmCardPayment(data.clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement)
        }
      });

      console.log("Payment confirmation result:", result);

      if (result.error) {
        console.error("Payment error:", result.error);
        throw new Error(result.error.message);
      }

      if (result.paymentIntent.status === 'succeeded') {
        console.log('Payment succeeded!');
        
        await fetch('/api/confirm-payment', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            orderId,
            tipAmount
          }),
        });

        setSucceeded(true);
        setLoading(false);
        onSuccess(tipAmount);
      }
    } catch (err) {
      console.error('Payment confirmation error:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#9e2146',
      },
    },
  };

  const handleTipSelection = (percentage) => {
    setCustomTipMode(false);
    const tipValue = (percentage / 100) * totalAmount;
    setTipAmount(parseFloat(tipValue.toFixed(2)));
  };

  const handleCustomTipChange = (e) => {
    const value = e.target.value;
    if (value === '' || (!isNaN(value) && parseFloat(value) >= 0)) {
      setTipAmount(value === '' ? 0 : parseFloat(value));
    }
  };

  return (
    <form onSubmit={handleSubmit} className={styles.checkoutForm}>
      <h3 className={styles.sectionTitle}>Payment Information</h3>
      
      <div className={styles.tipSection}>
        <h4>Add a tip</h4>
        <div className={styles.tipOptions}>
          <button 
            type="button" 
            className={`${styles.tipButton} ${tipAmount === parseFloat((totalAmount * 0.15).toFixed(2)) ? styles.selected : ''}`} 
            onClick={() => handleTipSelection(15)}
          >
            15% (${((totalAmount * 0.15).toFixed(2))})
          </button>
          <button 
            type="button" 
            className={`${styles.tipButton} ${tipAmount === parseFloat((totalAmount * 0.18).toFixed(2)) ? styles.selected : ''}`}
            onClick={() => handleTipSelection(18)}
          >
            18% (${((totalAmount * 0.18).toFixed(2))})
          </button>
          <button 
            type="button" 
            className={`${styles.tipButton} ${tipAmount === parseFloat((totalAmount * 0.20).toFixed(2)) ? styles.selected : ''}`}
            onClick={() => handleTipSelection(20)}
          >
            20% (${((totalAmount * 0.20).toFixed(2))})
          </button>
          <button 
            type="button" 
            className={`${styles.tipButton} ${customTipMode ? styles.selected : ''}`}
            onClick={() => setCustomTipMode(true)}
          >
            Custom
          </button>
        </div>
        
        {customTipMode && (
          <div className={styles.customTipInput}>
            <label>
              Custom tip amount:
              <div className={styles.currencyInput}>
                <span>$</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={tipAmount}
                  onChange={handleCustomTipChange}
                  className={styles.tipAmountInput}
                />
              </div>
            </label>
          </div>
        )}
      </div>

      <div className={styles.cardElementContainer}>
        <CardElement options={cardElementOptions} />
      </div>
      
      <div className={styles.orderSummary}>
        <div className={styles.summaryRow}>
          <span>Subtotal:</span>
          <span>${totalAmount.toFixed(2)}</span>
        </div>
        <div className={styles.summaryRow}>
          <span>Tip:</span>
          <span>${parseFloat(tipAmount).toFixed(2)}</span>
        </div>
        <div className={`${styles.summaryRow} ${styles.totalRow}`}>
          <span>Total:</span>
          <span>${totalWithTip.toFixed(2)}</span>
        </div>
      </div>
      
      {error && <div className={styles.errorMessage}>{error}</div>}
      
      <button 
        type="submit" 
        disabled={!stripe || loading || succeeded} 
        className={styles.payButton}
      >
        {loading ? 'Processing...' : `Pay $${totalWithTip.toFixed(2)}`}
      </button>
      
      {succeeded && (
        <div className={styles.successMessage}>
          Payment successful! Redirecting to confirmation page...
        </div>
      )}
    </form>
  );
};

// Main CheckoutPage component
const CheckoutPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stripeAccount, setStripeAccount] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  
  const urlTableParam = new URLSearchParams(location.search).get('table');
  const tableNumber = urlTableParam || null;

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const response = await fetch('/api/get-order', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ orderId }),
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to load order details');
        }
        
        const orderData = await response.json();
        setOrder(orderData);

        // Get the restaurant's Stripe account
        const { data: userProfile } = await supabase
          .from('user_profiles')
          .select('stripe_connect_id')
          .eq('user_id', orderData.restaurants.user_id)
          .single();

        if (userProfile?.stripe_connect_id) {
          setStripeAccount(userProfile.stripe_connect_id);
          
          // Initialize Stripe with the connected account for direct charges
          const stripe = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY, {
            stripeAccount: userProfile.stripe_connect_id
          });
          setStripePromise(stripe);
        }
      } catch (err) {
        console.error('Error fetching order:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchOrder();
  }, [orderId]);
  
  const handlePaymentSuccess = (tipAmount) => {
    setTimeout(() => {
      navigate(`/payment-success/${orderId}`);
    }, 1500);
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}></div>
        <p>Loading payment details...</p>
      </div>
    );
  }

  if (error || !order || !stripePromise) {
    return (
      <div className={styles.errorContainer}>
        <h2>Error Loading Checkout</h2>
        <p>{error || 'Order not found or payment system unavailable'}</p>
        <button 
          onClick={() => navigate('/')}
          className={styles.returnButton}
        >
          Return to Home
        </button>
      </div>
    );
  }

  return (
    <div className={styles.checkoutPage}>
      <div className={styles.checkoutHeader}>
        <h2>Complete Your Order</h2>
      </div>
      
      <div className={styles.orderSummaryCard}>
        <h3>Order Summary</h3>
        <div className={styles.restaurantInfo}>
          <strong>{order.restaurants?.name || 'Restaurant'}</strong>
          {tableNumber && (
            <span className={styles.tableNumber}>Table {tableNumber}</span>
          )}
        </div>
        
        <div className={styles.orderItems}>
          {order.order_items.map((item) => (
            <div key={item.id} className={styles.orderItem}>
              <div className={styles.itemInfo}>
                <span className={styles.itemQuantity}>{item.quantity}x</span>
                <span className={styles.itemName}>{item.menu_item_name}</span>
              </div>
              <span className={styles.itemPrice}>${(item.price * item.quantity).toFixed(2)}</span>
            </div>
          ))}
        </div>
        
        {order.special_instructions && (
          <div className={styles.specialInstructions}>
            <h4>Special Instructions:</h4>
            <p>{order.special_instructions}</p>
          </div>
        )}
      </div>
      
      <div className={styles.paymentContainer}>
        <Elements stripe={stripePromise}>
          <CheckoutForm 
            orderId={orderId}
            totalAmount={order.total_amount}
            restaurantId={order.restaurant_id}
            onSuccess={handlePaymentSuccess}
            stripeAccount={stripeAccount}
          />
        </Elements>
      </div>
    </div>
  );
};

export default CheckoutPage;